import MockAdapter from "axios-mock-adapter";

export type BillingPlanDto = {
  id: string;
  code: string;
  name: string;
  description: string;
  monthlyPrice: number;
  monthlyPriceIncVat: number;
  annualPrice: number;
  annualPriceIncVat: number;
  trialDays: number;
  maxUsers: number;
  annualSavings: number;
  annualSavingsPercentage: number;
};

const VAT_RATE = 0.15;
export const mockBillingPlans: BillingPlanDto[] = [
  createPlan({
    id: "11111111-1111-1111-1111-111111111111",
    code: "Recruitment",
    name: "Talent Recruitment",
    description: "Talent recruitment only",
    monthlyPrice: 100.0,
    annualPrice: 1200.0,
    trialDays: 7,
    maxUsers: 1,
  }),
  createPlan({
    id: "22222222-2222-2222-2222-222222222222",
    code: "Onboarding",
    name: "Employee Onboarding",
    description: "Employee onboarding only",
    monthlyPrice: 200.0,
    annualPrice: 2400.0,
    trialDays: 7,
    maxUsers: 1,
  }),
  createPlan({
    id: "33333333-3333-3333-3333-33333333",
    code: "TemperedTalent",
    name: "TemperedTalent Suite",
    description: "Complete suite with all features",
    monthlyPrice: 300.0,
    annualPrice: 3600.0,
    trialDays: 7,
    maxUsers: 1,
  }),
];

let planIdSeq = 1;

function createPlan(input: {
  id?: string;
  code: string;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  trialDays: number;
  maxUsers: number;
}): BillingPlanDto {
  const id = input.id ?? `plan-${planIdSeq++}`;
  const monthlyPriceIncVat = input.monthlyPrice * (1 + VAT_RATE);
  const annualPriceIncVat = input.annualPrice * (1 + VAT_RATE);
  const annualSavings = input.monthlyPrice * 12 - input.annualPrice;
  const annualSavingsPercentage =
    annualSavings === 0 ? 0 : (annualSavings / (input.monthlyPrice * 12)) * 100;

  return {
    id,
    code: input.code,
    name: input.name,
    description: input.description,
    monthlyPrice: input.monthlyPrice,
    monthlyPriceIncVat,
    annualPrice: input.annualPrice,
    annualPriceIncVat,
    trialDays: input.trialDays,
    maxUsers: input.maxUsers,
    annualSavings,
    annualSavingsPercentage,
  };
}

export function registerBillingPlanMocks(mock: MockAdapter) {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

  const listMatchers: (string | RegExp)[] = [
    "/billingplan/active",
    "/billing-plans",
    "/billing/plans",
    "/api/billing/plans",
    /billing.*plans/i,
  ];

  listMatchers
    .flatMap((p) =>
      typeof p === "string" && baseUrl ? [p, `${baseUrl}${p}`] : [p]
    )
    .forEach((m) => {
      if (m) mock.onGet(m).reply(200, mockBillingPlans);
    });

  mock.onGet(/\/api\/billing\/plans\/([^/?#]+)(\?.*)?$/).reply((config) => {
    const id = config.url?.split("/").pop()?.split("?")[0];
    const plan = mockBillingPlans.find((p) => p.id === id);
    if (!plan) return [404, { message: "Plan not found" }];
    return [200, plan];
  });
}
